//src/pages/inventory/Product/ProductsTable.jsx
import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  MenuItem,
  Tabs,
  Tab,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import { getAllInventory } from "../../../services/inventoryService.js";
import Header from "../../../components/Header.jsx";
import AddNewProduct from "./AddNewProduct.jsx";
import AddMedicineInventory from "./AddMedicineInventory.jsx";
import dayjs from "dayjs";
import { getMedicineInventoryList } from "../../../services/medicineInventoryService.js";
import { getAllMedicine /*, updateMedicine */ } from "../../../services/medicineNewService";
// Inventory cell-edit helpers kept for future restore:
// import {
//   getMedicineDisplayName,
//   validateUniqueCatalogName,
// } from "../../../utils/duplicateNameUtils";
import { useTheme } from "@emotion/react";
import AddNewMedicine from "./AddNewMedicine.jsx"; // Update the import
import EditMedicineModal from "./EditMedicineModal.jsx";
import OCRDialog from "./OCRDialog.jsx";
import OCRResultDialog from "./OCRResultDialog.jsx";
import { getInvoiceList } from "../../../services/invoiceService";
import { getAllSuppliers } from "../../../services/supplierService";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
// import SaveIcon from "@mui/icons-material/Save";
// import CancelIcon from "@mui/icons-material/Cancel";
import { processInvoiceWithOCR, addOCRStockToInventory } from "../../../services/ocrService";
import { useSelector } from 'react-redux';
import MedicineTransactionHistory from "../Pharmacy/MedicineTransactionHistory";
import SupplierDetailsTable from "./SupplierDetailsTable";
import PharmacyExpiryThresholdSettings from "../../../components/PharmacyExpiryThresholdSettings.jsx";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import { usePharmacyExpiryThreshold } from "../../../hooks/usePharmacyExpiryThreshold";
import {
  applyExpiryFlagsToGroupedProducts,
  isExpiredExpiryDate,
  readExpiryThresholdMonthsFromStorage,
} from "../../../utils/medicineExpiryThreshold";
import { readLowStockQuantityThreshold } from "../../../utils/medicineLowStock";
import { getOrganizationId, getOrganizationName } from "../../../utils/organizationUtils";
import { getAllMedicineTransactions } from "../../../services/medicineTransactionService";
import {
  downloadMedicineInventoryTableExcel,
  downloadStyledTableExcel,
} from "../../../utils/patientTableExcelExport";
import { buildMedicineInventoryExportTable } from "../../../utils/inventoryBatchExportUtils";
import { buildMedicineInventoryExportSummary } from "../../../utils/medicineInventorySummary";
import {
  getGroupInventoryBadgeKeys,
  getInventoryNameColor,
  getStockStatus,
  InventoryStatusBadges,
  InventoryStatusLegend,
} from "../../../components/InventoryStatusBadges";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
} from "../../../utils/pageLayoutStyles";

const columns = [
  { id: "expand", label: "", width: 50 },
  { id: "name", label: "Name" },
  { id: "medicineType", label: "Category" },
  { id: "medicineDetails", label: "Type" },
  { id: "price", label: "Price" },
  { id: "quantity", label: "Total Quantity" },
];

// Additional columns for expanded rows only
const expandedColumns = [
  { id: "expand", label: "", width: 50 },
  { id: "inventoryId", label: "ID" },
  { id: "name", label: "Name" },
  { id: "medicineType", label: "Category" },
  { id: "medicineDetails", label: "Type" },
  { id: "price", label: "Price" },
  { id: "quantity", label: "Quantity" },
  { id: "batchInfo", label: "Batch Info" },
];

const normalizeInventoryMatchValue = (value) =>
  String(value || "").trim().toLowerCase();

const getInventoryRecordBatchNumber = (record) =>
  record?.batchNumber || record?.batch_number || record?.batchNo || "";

const getInventoryGroupNameCandidates = (group) =>
  [group?.groupKey, group?.name, group?.medicineName]
    .map(normalizeInventoryMatchValue)
    .filter(Boolean);

const inventoryBatchNumbersMatch = (left, right) => {
  const a = normalizeInventoryMatchValue(left);
  const b = normalizeInventoryMatchValue(right);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
};

const inventoryGroupHasBatch = (group, batchNumber) => {
  const batch = normalizeInventoryMatchValue(batchNumber);
  if (!batch) return false;
  return (group?.records || []).some((record) =>
    inventoryBatchNumbersMatch(getInventoryRecordBatchNumber(record), batch)
  );
};

const inventoryGroupNamesMatch = (group, medicineName, { exact = false } = {}) => {
  const target = normalizeInventoryMatchValue(medicineName);
  if (!target) return false;
  return getInventoryGroupNameCandidates(group).some((candidate) => {
    if (candidate === target) return true;
    if (exact) return false;
    return candidate.includes(target) || target.includes(candidate);
  });
};

const findInventoryGroupForTransaction = (
  productList,
  { medicineName, batchNumber, medicineId, medicineInventoryId } = {}
) => {
  const list = Array.isArray(productList) ? productList : [];
  const inventoryId =
    medicineInventoryId != null && medicineInventoryId !== ""
      ? String(medicineInventoryId)
      : "";
  const medId =
    medicineId != null && medicineId !== "" ? String(medicineId) : "";

  const recordMatchesInventoryId = (record) =>
    Boolean(inventoryId) &&
    [record?.id, record?.medicineInventoryId, record?.medicine_inventory_id]
      .filter((value) => value != null && value !== "")
      .some((value) => String(value) === inventoryId);

  const groupMatchesMedicineId = (group) =>
    Boolean(medId) &&
    (String(group?.medicineId) === medId ||
      (group?.records || []).some(
        (record) => String(record?.medicineId) === medId
      ));

  if (inventoryId) {
    const matched = list.find((group) =>
      (group.records || []).some(recordMatchesInventoryId)
    );
    if (matched) return matched;
  }

  if (medId && batchNumber) {
    const matched = list.find(
      (group) =>
        groupMatchesMedicineId(group) &&
        inventoryGroupHasBatch(group, batchNumber)
    );
    if (matched) return matched;
  }

  if (medicineName && batchNumber) {
    const matched = list.find(
      (group) =>
        inventoryGroupNamesMatch(group, medicineName) &&
        inventoryGroupHasBatch(group, batchNumber)
    );
    if (matched) return matched;
  }

  if (medicineName) {
    const exact = list.find((group) =>
      inventoryGroupNamesMatch(group, medicineName, { exact: true })
    );
    if (exact) return exact;
  }

  if (medId) {
    const matched = list.find(groupMatchesMedicineId);
    if (matched) return matched;
  }

  if (medicineName) {
    const loose = list.find((group) =>
      inventoryGroupNamesMatch(group, medicineName)
    );
    if (loose) return loose;
  }

  if (batchNumber) {
    const matched = list.find((group) =>
      inventoryGroupHasBatch(group, batchNumber)
    );
    if (matched) return matched;
  }

  return null;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

export default function ProductsTable({ hideMainTitle = false }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rawMedicinesPage, setRawMedicinesPage] = useState(0); // Pagination state for raw medicines
  const [rawMedicinesRowsPerPage, setRawMedicinesRowsPerPage] = useState(10); // Rows per page for raw medicines
  // Single fetching state object for all tabs
  const [fetching, setFetching] = useState({
    product_list: true,
    raw_medicines: false,
    inventory_history: false,
  });
  const [product_list, setProductList] = useState([]);
  const [medicineData, setMedicineData] = useState([]); // Store raw medicine data
  const [rawMedicines, setRawMedicines] = useState([]); // Store raw medicines for Raw Medicines tab
  const theme = useTheme();
  const user = useSelector((state) => state.user.user); // Get user from Redux
  const { user: authUser } = useAuth();
  const { hasCapability } = useSubscriptionCapabilities();
  const {
    getExpiryStatus: getExpiryStatusFromThreshold,
    months: expiryThresholdMonths,
    loading: expiryThresholdLoading,
  } = usePharmacyExpiryThreshold();
  const hasMedicineInventoryCapability = hasCapability("Medicine Inventory");
  const showAdminMedicineTabs =
    (authUser?.roleName === "admin" ||
      authUser?.roleName === "superadmin" ||
      authUser?.roleName === "pharmacy admin") &&
    hasMedicineInventoryCapability;

  const MEDICINE_TAB = {
    INVENTORY: 0,
    TRANSACTION_HISTORY: 1,
    RAW_MEDICINES: 2,
    SUPPLIER_DETAILS: 3,
    NEAR_EXPIRY_SETTINGS: 4,
  };
  const [open_product_modal, setOpenProductModal] = useState(false);
  const [product_info, setProductInfo] = useState(null);
  const [open_medicine_modal, setOpenMedicineModal] = useState(false); // New state for the medicine modal
  const [open_edit_medicine_modal, setOpenEditMedicineModal] = useState(false); // New state for the medicine edit modal
  const [open_ocr_dialog, setOpenOCRDialog] = useState(false); // State for OCR dialog
  const [open_ocr_result_dialog, setOpenOCRResultDialog] = useState(false); // State for OCR result dialog
  const [ocrData, setOCRData] = useState(null); // State for OCR extracted data
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterManufacturer, setFilterManufacturer] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [filterPriceMin, setFilterPriceMin] = useState("");
  const [filterPriceMax, setFilterPriceMax] = useState("");
  const [filterQuantityMin, setFilterQuantityMin] = useState("");
  const [filterQuantityMax, setFilterQuantityMax] = useState("");
  const [filterBatchNumber, setFilterBatchNumber] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  // Search and filter states for Raw Medicines tab
  const [rawMedicinesSearchQuery, setRawMedicinesSearchQuery] = useState("");
  const [rawMedicinesFilterName, setRawMedicinesFilterName] = useState("");
  const [rawMedicinesFilterCategory, setRawMedicinesFilterCategory] = useState("");
  const [rawMedicinesFilterType, setRawMedicinesFilterType] = useState("");
  const [rawMedicinesFilterManufacturer, setRawMedicinesFilterManufacturer] = useState("");
  const [rawMedicinesFilterPriceMin, setRawMedicinesFilterPriceMin] = useState("");
  const [rawMedicinesFilterPriceMax, setRawMedicinesFilterPriceMax] = useState("");
  const [rawMedicinesFilterCostMin, setRawMedicinesFilterCostMin] = useState("");
  const [rawMedicinesFilterCostMax, setRawMedicinesFilterCostMax] = useState("");
  const [rawMedicinesFilterDetails, setRawMedicinesFilterDetails] = useState("");
  const [rawMedicinesShowFilters, setRawMedicinesShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // State for tab management
  const [addStockMenuAnchor, setAddStockMenuAnchor] = useState(null); // State for add stock menu
  
  // Medicine Inventory History tab states
  const [inventoryHistoryPage, setInventoryHistoryPage] = useState(0);
  const [inventoryHistoryRowsPerPage, setInventoryHistoryRowsPerPage] = useState(10);
  const [inventoryHistory, setInventoryHistory] = useState([]); // Store invoice data with supplier info
  const [inventoryHistorySearchQuery, setInventoryHistorySearchQuery] = useState("");
  const [inventoryHistoryShowFilters, setInventoryHistoryShowFilters] = useState(false);
  const [inventoryHistoryFilterInvoiceNumber, setInventoryHistoryFilterInvoiceNumber] = useState("");
  const [inventoryHistoryFilterSupplierName, setInventoryHistoryFilterSupplierName] = useState("");
  const [inventoryHistoryFilterDateFrom, setInventoryHistoryFilterDateFrom] = useState("");
  const [inventoryHistoryFilterDateTo, setInventoryHistoryFilterDateTo] = useState("");
  
  // Expandable rows states
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track which rows are expanded
  // Inventory inline edit (disabled for now — restore with cell handlers below)
  // const [editingCell, setEditingCell] = useState(null);
  // const [editingValue, setEditingValue] = useState("");
  // const [originalValue, setOriginalValue] = useState("");
  // const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProductList();
  }, []);

  // Re-apply near-expiry flags when threshold loads or changes (e.g. saved as 1 month)
  useEffect(() => {
    if (expiryThresholdLoading) return;
    setProductList((prev) => {
      if (!prev?.length) return prev;
      return applyExpiryFlagsToGroupedProducts(prev, expiryThresholdMonths);
    });
  }, [expiryThresholdMonths, expiryThresholdLoading]);

  // Fetch raw medicines for Raw Medicines tab
  const fetchRawMedicines = async () => {
    setFetching((prev) => ({ ...prev, raw_medicines: true }));
    try {
      const data = await getAllMedicine();
      // Sort by ID in descending order (newest first)
      const sortedData = Array.isArray(data) ? data.sort((a, b) => (b.id || 0) - (a.id || 0)) : [];
      setRawMedicines(sortedData);
    } catch (error) {
      console.error("Error fetching raw medicines:", error);
      setRawMedicines([]);
    }
    setFetching((prev) => ({ ...prev, raw_medicines: false }));
  };

  // Fetch raw medicines when Raw Medicines tab is selected
  useEffect(() => {
    if (activeTab === MEDICINE_TAB.RAW_MEDICINES) {
      fetchRawMedicines();
    }
  }, [activeTab]);

  // const fetchProductList = async () => {
  //   setFetching({ ...fetching, product_list: true });
  //   try {
  //     const data = await getMedicineInventoryList();
  //     // setProductList(data);
      
  //     // Sort the data by id in descending order
  //     const sortedData = data.sort((a, b) => b.id - a.id);
      
  //     setProductList(sortedData);
  //   } catch (error) {
  //     console.log(error);
  //   }
  //   setFetching({ ...fetching, product_list: false });
  // };

  const isExpired = (expiryDate) => isExpiredExpiryDate(expiryDate);

  const getExpiryStatus = (expiryDate) => getExpiryStatusFromThreshold(expiryDate);

  const stockThreshold = readLowStockQuantityThreshold(
    getOrganizationId(user) || user?.organizationId
  );

  const getNameColor = (group) =>
    getInventoryNameColor({
      expiryStatus: group?.worstExpiryStatus,
      stockStatus: getStockStatus(group?.totalQuantity, stockThreshold),
    });

  const resolveSupplierDisplayName = (value) => {
    if (value == null || value === "") return "";
    if (typeof value === "string" || typeof value === "number") {
      const text = String(value).trim();
      // Numeric-only values are supplier IDs, not display names
      if (/^\d+$/.test(text)) return "";
      return text;
    }
    if (typeof value === "object") {
      return resolveSupplierDisplayName(
        value.name || value.supplierName || value.supplier_name || ""
      );
    }
    return "";
  };

  const fetchProductList = async () => {
    setFetching({ ...fetching, product_list: true });
    try {
      const [inventoryData, medicineData, invoiceData, suppliersData] =
        await Promise.all([
          getMedicineInventoryList(),
          getAllMedicine(),
          getInvoiceList({ fullRange: true }).catch(() => []),
          getAllSuppliers().catch(() => []),
        ]);
      
      // Create a map for medicine details
      const medicineMap = medicineData.reduce((acc, medicine) => {
        acc[medicine.id] = medicine; // Map medicineId to medicine object
        return acc;
      }, {});

      const invoices = Array.isArray(invoiceData) ? invoiceData : [];
      const invoiceMetaById = invoices.reduce((acc, invoice) => {
        const id = invoice?.id ?? invoice?.invoiceId;
        if (id == null) return acc;
        const supplierName =
          resolveSupplierDisplayName(invoice?.supplierName) ||
          resolveSupplierDisplayName(invoice?.supplier_name) ||
          resolveSupplierDisplayName(invoice?.fromName) ||
          resolveSupplierDisplayName(invoice?.from_name) ||
          "";
        const invoiceNumber = String(
          invoice?.invoiceNumber || invoice?.invoice_number || ""
        ).trim();
        acc[String(id)] = { supplierName, invoiceNumber };
        return acc;
      }, {});

      const suppliers = Array.isArray(suppliersData) ? suppliersData : [];
      const supplierNameById = suppliers.reduce((acc, supplier) => {
        const id = supplier?.id ?? supplier?.supplierId;
        if (id == null) return acc;
        const name =
          resolveSupplierDisplayName(supplier?.name) ||
          resolveSupplierDisplayName(supplier?.supplierName) ||
          "";
        if (name) acc[String(id)] = name;
        return acc;
      }, {});

      const resolveRecordInvoiceNumber = (current) => {
        const direct = String(
          current.invoiceNumber || current.invoice_number || ""
        ).trim();
        if (direct) return direct;
        const invoiceId = current.invoiceId ?? current.invoice_id;
        if (invoiceId == null) return "";
        return invoiceMetaById[String(invoiceId)]?.invoiceNumber || "";
      };

      const resolveRecordSupplierName = (current) => {
        const medicineSupplier = resolveSupplierDisplayName(
          medicineMap[current.medicineId]?.supplier
        );
        const supplierId =
          current.supplierId ??
          current.supplier_id ??
          (typeof current.supplier === "object" ? current.supplier?.id : null) ??
          (/^\d+$/.test(String(current.supplier ?? "").trim())
            ? current.supplier
            : null);
        const invoiceId = current.invoiceId ?? current.invoice_id;

        return (
          resolveSupplierDisplayName(current.supplierName) ||
          resolveSupplierDisplayName(current.supplier_name) ||
          resolveSupplierDisplayName(current.fromName) ||
          resolveSupplierDisplayName(current.from_name) ||
          resolveSupplierDisplayName(current.supplier) ||
          (invoiceId != null
            ? invoiceMetaById[String(invoiceId)]?.supplierName || ""
            : "") ||
          (supplierId != null ? supplierNameById[String(supplierId)] || "" : "") ||
          medicineSupplier ||
          ""
        );
      };
      
      // Group by name for display, but keep individual records
      const groupedData = inventoryData.reduce((acc, current) => {
        const key = current.name; // Group by name
        const expiryDateValue = current.expirationDate || current.expiryDate || current.expiry;
        const expired = isExpired(expiryDateValue);
        const expiryStatus = getExpiryStatus(expiryDateValue);
        const supplierName = resolveRecordSupplierName(current);
        const invoiceNumber = resolveRecordInvoiceNumber(current);
        const recordWithMeta = { ...current, supplierName, invoiceNumber };
        
        if (!acc[key]) {
          acc[key] = {
            groupKey: key,
            id: current.id, // Use the first record's inventory ID
            name: key, // Add name field for display
            totalQuantity: expired ? 0 : current.quantity, // Don't count expired stock
            totalQuantityIncludingExpired: current.quantity, // Track total including expired
            hasExpiredStock: expired,
            hasNearingExpiry: expiryStatus === 'nearing',
            hasGoodStock: expiryStatus === 'good',
            worstExpiryStatus: expiryStatus, // Track worst status for color priority
            records: [recordWithMeta], // Store individual records
            // Add medicine details from the first record
            medicineId: current.medicineId,
            medicineName: medicineMap[current.medicineId]?.medicineName || '',
            medicineType: medicineMap[current.medicineId]?.medicineType || '',
            manufacturer: medicineMap[current.medicineId]?.manufacturer || '',
            supplier:
              supplierName ||
              resolveSupplierDisplayName(medicineMap[current.medicineId]?.supplier) ||
              '',
            medicineDetails: medicineMap[current.medicineId]?.medicineDetails || '',
            details: medicineMap[current.medicineId]?.details || '',
            price: current.price || 0, // Use inventory table price instead of raw medicine price
            costPerUnit: medicineMap[current.medicineId]?.costPerUnit || '',
          };
        } else {
          acc[key].totalQuantity += expired ? 0 : current.quantity; // Don't count expired stock
          acc[key].totalQuantityIncludingExpired += current.quantity; // Track total including expired
          acc[key].hasExpiredStock = acc[key].hasExpiredStock || expired; // Mark if any expired stock exists
          acc[key].hasNearingExpiry = acc[key].hasNearingExpiry || expiryStatus === 'nearing';
          acc[key].hasGoodStock = acc[key].hasGoodStock || expiryStatus === 'good';
          
          // Update worst status with priority: expired > nearing > good
          if (expiryStatus === 'expired') {
            acc[key].worstExpiryStatus = 'expired';
          } else if (expiryStatus === 'nearing' && acc[key].worstExpiryStatus !== 'expired') {
            acc[key].worstExpiryStatus = 'nearing';
          } else if (expiryStatus === 'good' && acc[key].worstExpiryStatus !== 'expired' && acc[key].worstExpiryStatus !== 'nearing') {
            acc[key].worstExpiryStatus = 'good';
          }
          
          acc[key].records.push(recordWithMeta); // Add to records array
          if (!acc[key].supplier && supplierName) {
            acc[key].supplier = supplierName;
          }
          // Update price to show the latest inventory record's price
          acc[key].price = current.price || 0;
        }
        return acc;
      }, {});
      
      // Convert the grouped object back to an array
      const result = Object.values(groupedData);
      
      
      // Sort the data by ID in descending order
      const sortedData = result.sort((a, b) => b.id - a.id);
      const thresholdMonths =
        readExpiryThresholdMonthsFromStorage(getOrganizationId(authUser)) ??
        expiryThresholdMonths;
      setProductList(applyExpiryFlagsToGroupedProducts(sortedData, thresholdMonths));
      setMedicineData(medicineData); // Store raw medicine data
    } catch (error) {
      console.log(error);
    }
    setFetching({ ...fetching, product_list: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRawMedicinesChangePage = (event, newPage) => {
    setRawMedicinesPage(newPage);
  };

  const handleRawMedicinesChangeRowsPerPage = (event) => {
    setRawMedicinesRowsPerPage(+event.target.value);
    setRawMedicinesPage(0);
  };

  const filteredRows = product_list.filter((p) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      // Include batch numbers from all records in the search
      const batchNumbers = p.records ? p.records.map(r => String(r.batchNumber || '')).filter(Boolean) : [];
      const vals = [
        p.id, 
        p.name, 
        p.medicineType, 
        p.medicineDetails, 
        p.manufacturer,
        p.supplier,
        p.price, 
        p.totalQuantity,
        ...batchNumbers
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!vals.some((v) => v.includes(q))) return false;
    }
    if (filterName.trim()) {
      if (!String(p.name || "").toLowerCase().includes(filterName.toLowerCase())) return false;
    }
    if (filterCategory) {
      if (String(p.medicineType || "") !== filterCategory) return false;
    }
    if (filterType) {
      if (String(p.medicineDetails || "") !== filterType) return false;
    }
    if (filterManufacturer.trim()) {
      if (!String(p.manufacturer || "").toLowerCase().includes(filterManufacturer.toLowerCase())) return false;
    }
    if (filterSupplier.trim()) {
      if (!String(p.supplier || "").toLowerCase().includes(filterSupplier.toLowerCase())) return false;
    }
    if (filterPriceMin !== "") {
      const price = Number(p.price || 0);
      if (price < Number(filterPriceMin)) return false;
    }
    if (filterPriceMax !== "") {
      const price = Number(p.price || 0);
      if (price > Number(filterPriceMax)) return false;
    }
    if (filterQuantityMin !== "") {
      const qty = Number(p.totalQuantity || 0);
      if (qty < Number(filterQuantityMin)) return false;
    }
    if (filterQuantityMax !== "") {
      const qty = Number(p.totalQuantity || 0);
      if (qty > Number(filterQuantityMax)) return false;
    }
    if (filterBatchNumber.trim()) {
      // Check if any record in the group has a matching batch number
      const batchNumberFilter = filterBatchNumber.toLowerCase().trim();
      const hasMatchingBatch = p.records && p.records.some((record) => {
        const recordBatchNumber = String(record.batchNumber || '').toLowerCase();
        return recordBatchNumber.includes(batchNumberFilter);
      });
      if (!hasMatchingBatch) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterCategory("");
    setFilterType("");
    setFilterManufacturer("");
    setFilterSupplier("");
    setFilterPriceMin("");
    setFilterPriceMax("");
    setFilterQuantityMin("");
    setFilterQuantityMax("");
    setFilterBatchNumber("");
  };

  const clearRawMedicinesFilters = () => {
    setRawMedicinesSearchQuery("");
    setRawMedicinesFilterName("");
    setRawMedicinesFilterCategory("");
    setRawMedicinesFilterType("");
    setRawMedicinesFilterManufacturer("");
    setRawMedicinesFilterPriceMin("");
    setRawMedicinesFilterPriceMax("");
    setRawMedicinesFilterCostMin("");
    setRawMedicinesFilterCostMax("");
    setRawMedicinesFilterDetails("");
  };

  // Filter inventory history based on search and filters
  const filteredInventoryHistory = inventoryHistory.filter((invoice) => {
    if (inventoryHistorySearchQuery.trim()) {
      const q = inventoryHistorySearchQuery.toLowerCase();
      const vals = [
        invoice.id,
        invoice.invoiceNumber,
        invoice.supplierName,
        invoice.supplierContact,
        invoice.supplierGstNumber,
        invoice.supplierAddress,
        invoice.invoiceType,
        invoice.amount,
        invoice.discount,
        invoice.sgst,
        invoice.cgst,
        invoice.totalAmount,
        invoice.userId
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!vals.some((v) => v.includes(q))) return false;
    }
    if (inventoryHistoryFilterInvoiceNumber.trim()) {
      const invoiceNumber = String(invoice.invoiceNumber || '').toLowerCase();
      if (!invoiceNumber.includes(inventoryHistoryFilterInvoiceNumber.toLowerCase())) return false;
    }
    if (inventoryHistoryFilterSupplierName.trim()) {
      const supplierName = String(invoice.supplierName || '').toLowerCase();
      if (!supplierName.includes(inventoryHistoryFilterSupplierName.toLowerCase())) return false;
    }
    if (inventoryHistoryFilterDateFrom) {
      const invoiceDate = invoice.date ? dayjs(invoice.date) : null;
      const filterDateFrom = dayjs(inventoryHistoryFilterDateFrom);
      if (!invoiceDate || invoiceDate.isBefore(filterDateFrom, 'day')) return false;
    }
    if (inventoryHistoryFilterDateTo) {
      const invoiceDate = invoice.date ? dayjs(invoice.date) : null;
      const filterDateTo = dayjs(inventoryHistoryFilterDateTo);
      if (!invoiceDate || invoiceDate.isAfter(filterDateTo, 'day')) return false;
    }
    return true;
  });

  // Filter raw medicines based on search and filters
  const filteredRawMedicines = rawMedicines.filter((medicine) => {
    if (rawMedicinesSearchQuery.trim()) {
      const q = rawMedicinesSearchQuery.toLowerCase();
      const vals = [
        medicine.id,
        medicine.medicineName || medicine.name,
        medicine.medicineType,
        medicine.medicineDetails,
        medicine.manufacturer,
        medicine.price,
        medicine.costPerUnit
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!vals.some((v) => v.includes(q))) return false;
    }
    if (rawMedicinesFilterName.trim()) {
      const medicineName = (medicine.medicineName || medicine.name || "").toLowerCase();
      if (!medicineName.includes(rawMedicinesFilterName.toLowerCase())) return false;
    }
    if (rawMedicinesFilterCategory) {
      if (String(medicine.medicineType || "") !== rawMedicinesFilterCategory) return false;
    }
    if (rawMedicinesFilterType) {
      if (String(medicine.medicineDetails || "") !== rawMedicinesFilterType) return false;
    }
    if (rawMedicinesFilterManufacturer.trim()) {
      const manufacturer = String(medicine.manufacturer || "").toLowerCase();
      if (!manufacturer.includes(rawMedicinesFilterManufacturer.toLowerCase())) return false;
    }
    if (rawMedicinesFilterPriceMin !== "") {
      const price = Number(medicine.price || 0);
      if (price < Number(rawMedicinesFilterPriceMin)) return false;
    }
    if (rawMedicinesFilterPriceMax !== "") {
      const price = Number(medicine.price || 0);
      if (price > Number(rawMedicinesFilterPriceMax)) return false;
    }
    if (rawMedicinesFilterCostMin !== "") {
      const cost = Number(medicine.costPerUnit || 0);
      if (cost < Number(rawMedicinesFilterCostMin)) return false;
    }
    if (rawMedicinesFilterCostMax !== "") {
      const cost = Number(medicine.costPerUnit || 0);
      if (cost > Number(rawMedicinesFilterCostMax)) return false;
    }
    if (rawMedicinesFilterDetails.trim()) {
      const details = String(medicine.details || "").toLowerCase();
      if (!details.includes(rawMedicinesFilterDetails.toLowerCase())) return false;
    }
    return true;
  });

  const handleExport = async () => {
    const rows = filteredRows || [];
    const { headers, tableRows } = buildMedicineInventoryExportTable(rows);

    let transactions = [];
    try {
      const data = await getAllMedicineTransactions();
      transactions = Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn("Could not load medicine transactions for export summary:", error);
    }

    await downloadMedicineInventoryTableExcel({
      filename: `medicine_inventory_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Medicine Inventory",
      organizationName: getOrganizationName(authUser || user),
      summaryTitle: "Medicine Inventory Summary",
      periodLabel: "All time",
      summary: buildMedicineInventoryExportSummary({
        inventoryRows: rows,
        transactions,
      }),
      tableHeaders: headers,
      tableRows,
    });
  };

  const handleRawMedicinesExport = async () => {
    const rows = filteredRawMedicines || [];
    const headers = ["ID", "Medicine Name", "Category", "Type", "Manufacturer", "Price", "Cost Per Unit", "Details"];
    const tableRows = rows.map((r) => [
      r.id,
      r.medicineName || r.name || "",
      r.medicineType || "",
      r.medicineDetails || "",
      r.manufacturer || "",
      r.price || 0,
      r.costPerUnit || 0,
      r.details || "",
    ]);

    await downloadStyledTableExcel({
      filename: `raw_medicines_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Raw Medicines",
      title: "Raw Medicines",
      organizationName: getOrganizationName(authUser || user),
      tableHeaders: headers,
      tableRows,
    });
  };

  const handleOCRComplete = (extractedData) => {
    setOCRData(extractedData);
    setOpenOCRResultDialog(true);
  };

  const handleOCRConfirm = async (confirmedData) => {
    try {
      // Add the OCR extracted stock to inventory with user information
      const result = await addOCRStockToInventory(confirmedData, user);
      
      // Refresh the product list
      await fetchProductList();
      
      // Close dialogs
      setOpenOCRResultDialog(false);
      setOCRData(null);
      
      // Show success message with detailed information
      console.log("Stock added successfully from OCR data:", result);
      
      // You can add a toast notification here to show the result
      if (result.newMedicinesCreated && result.newMedicinesCreated.length > 0) {
        console.log(`New medicines created: ${result.newMedicinesCreated.map(m => m.medicine).join(', ')}`);
      }
      
    } catch (error) {
      console.error("Error adding OCR stock:", error);
      throw error; // This will be caught by the OCRResultDialog
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddStockMenuOpen = (event) => {
    setAddStockMenuAnchor(event.currentTarget);
  };

  const handleAddStockMenuClose = () => {
    setAddStockMenuAnchor(null);
  };

  const handleManualStockAdd = () => {
    setOpenProductModal(true);
    handleAddStockMenuClose();
  };

  const handleOCRStockAdd = () => {
    setOpenOCRDialog(true);
    handleAddStockMenuClose();
  };

  // Expandable rows functions
  const toggleRowExpansion = (groupKey) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(groupKey)) {
      newExpandedRows.delete(groupKey);
    } else {
      newExpandedRows.add(groupKey);
    }
    setExpandedRows(newExpandedRows);
  };

  // Inventory fields are read-only for now. Restore the commented handlers below to re-enable.
  const renderEditableCell = (record, fieldName, value) => {
    const displayValue =
      value !== null && value !== undefined && value !== ""
        ? fieldName === "expirationDate" && value
          ? dayjs(value).format("YYYY-MM-DD")
          : value
        : fieldName === "batchNumber" || fieldName === "expirationDate"
          ? "N/A"
          : "-";
    return <Box component="span">{displayValue}</Box>;
  };

  /* DISABLED: individual inventory field editing (restore when needed)
  // Individual cell editing functions
  const handleCellClick = (inventoryId, columnId, currentValue) => {
    if (editingCell) return; // Prevent multiple edits
    
    setEditingCell({ inventoryId, columnId });
    setEditingValue(currentValue || "");
    setOriginalValue(currentValue || "");
  };

  const handleCellChange = (event) => {
    setEditingValue(event.target.value);
  };

  const handleCellSave = async () => {
    if (!editingCell) return;
    
    setSaving(true);
    try {
      const { inventoryId, columnId } = editingCell;
      
      // Find the specific inventory record
      let targetRecord = null;
      let groupIndex = -1;
      let recordIndex = -1;
      
      for (let i = 0; i < product_list.length; i++) {
        const group = product_list[i];
        for (let j = 0; j < group.records.length; j++) {
          if (group.records[j].id === inventoryId) {
            targetRecord = group.records[j];
            groupIndex = i;
            recordIndex = j;
            break;
          }
        }
        if (targetRecord) break;
      }
      
      if (!targetRecord) {
        throw new Error("Record not found");
      }

      if (columnId === "name") {
        const trimmedName = String(editingValue || "").trim();
        if (!trimmedName) {
          alert("Medicine name is required.");
          return;
        }

        let medicines = medicineData;
        try {
          const fresh = await getAllMedicine();
          medicines = Array.isArray(fresh) ? fresh : [];
        } catch (error) {
          console.warn("[ProductsTable] Could not refresh medicines for duplicate check:", error);
        }

        const duplicateError = validateUniqueCatalogName({
          name: trimmedName,
          items: medicines,
          excludeId: targetRecord.medicineId,
          getName: getMedicineDisplayName,
          getId: (item) => item?.id,
          entityLabel: "Medicine",
        });
        if (duplicateError) {
          alert(duplicateError);
          return;
        }
      }

      // Update only the specific field in the specific record
      const updatedProductList = [...product_list];
      let localUpdatedValue = editingValue;
      
      // Handle different field types for local state
      if (columnId === 'quantity') {
        localUpdatedValue = parseInt(editingValue) || 0;
      } else if (columnId === 'price') {
        localUpdatedValue = parseFloat(editingValue) || 0;
      } else if (columnId === 'expirationDate') {
        localUpdatedValue = editingValue ? editingValue : null;
      } else if (columnId === 'batchNumber') {
        localUpdatedValue = editingValue === 'N/A' ? null : editingValue;
      }
      
      updatedProductList[groupIndex].records[recordIndex] = {
        ...updatedProductList[groupIndex].records[recordIndex],
        [columnId]: localUpdatedValue
      };
      
      // Recalculate total quantity if quantity was changed (exclude expired stock)
      if (columnId === 'quantity' || columnId === 'expirationDate') {
        updatedProductList[groupIndex].totalQuantity = updatedProductList[groupIndex].records.reduce(
          (sum, record) => {
            const expiryDateValue = record.expirationDate || record.expiryDate || record.expiry;
            const expired = isExpired(expiryDateValue);
            return sum + (expired ? 0 : (parseInt(record.quantity) || 0));
          }, 0
        );
        
        // Recalculate expiry status flags
        let hasExpired = false;
        let hasNearing = false;
        let hasGood = false;
        let worstStatus = 'good';
        
        updatedProductList[groupIndex].records.forEach(record => {
          const expiryDateValue = record.expirationDate || record.expiryDate || record.expiry;
          const status = getExpiryStatus(expiryDateValue);
          
          if (status === 'expired') {
            hasExpired = true;
            worstStatus = 'expired';
          } else if (status === 'nearing') {
            hasNearing = true;
            if (worstStatus !== 'expired') worstStatus = 'nearing';
          } else if (status === 'good') {
            hasGood = true;
            if (worstStatus !== 'expired' && worstStatus !== 'nearing') worstStatus = 'good';
          }
        });
        
        updatedProductList[groupIndex].hasExpiredStock = hasExpired;
        updatedProductList[groupIndex].hasNearingExpiry = hasNearing;
        updatedProductList[groupIndex].hasGoodStock = hasGood;
        updatedProductList[groupIndex].worstExpiryStatus = worstStatus;
      }
      
      // Update group price if price was changed (use the latest record's price)
      if (columnId === 'price') {
        updatedProductList[groupIndex].price = localUpdatedValue;
      }
      
      // Update group name if medicine name was changed
      if (columnId === 'name') {
        updatedProductList[groupIndex].name = localUpdatedValue;
        updatedProductList[groupIndex].groupKey = localUpdatedValue;
      }
      
      setProductList(updatedProductList);

      // Update the backend - send all existing fields to prevent data loss
      let updatedValue = editingValue;
      
      // Handle different field types
      if (columnId === 'quantity') {
        updatedValue = parseInt(editingValue) || 0;
      } else if (columnId === 'price') {
        // Handle price - convert to number
        updatedValue = parseFloat(editingValue) || 0;
      } else if (columnId === 'expirationDate') {
        // Handle date format - if user enters date, keep it; if empty, set to null
        updatedValue = editingValue ? editingValue : null;
      } else if (columnId === 'batchNumber') {
        // Handle batch number - if user enters 'N/A', convert to null
        updatedValue = editingValue === 'N/A' ? null : editingValue;
      }
      
      // Create update data with proper field mapping
      const updateData = {
        ...targetRecord, // Include all existing fields
        [columnId]: updatedValue
      };
      
      // Always fix field name mapping for backend compatibility
      // Remove expirationDate and use expiryDate instead (regardless of which field is being edited)
      if (updateData.expirationDate !== undefined) {
        updateData.expiryDate = updateData.expirationDate;
        delete updateData.expirationDate;
      }
      
      // Import the update function
      const { updateMedicineInventory } = await import("../../../services/medicineInventoryService");
      await updateMedicineInventory(inventoryId, updateData);

      // If medicine name was updated, also update the medicine_raw_details table
      if (columnId === 'name') {
        try {
          // Find the current medicine data from the raw medicine table (base table)
          // This ensures we preserve the original price and other fields from the raw medicine table
          const currentMedicine = medicineData.find(med => med.id === targetRecord.medicineId);
          
          if (!currentMedicine) {
            console.warn(`Medicine with ID ${targetRecord.medicineId} not found in raw medicine data`);
            return; // Don't update if medicine not found
          }
          
          // Only update the medicine name, keep all other fields from the raw medicine table unchanged
          const medicineUpdateData = {
            id: targetRecord.medicineId,
            medicineName: updatedValue, // Only update the name
            // Keep all other fields from the raw medicine table (especially price)
            medicineType: currentMedicine.medicineType || '',
            medicineDetails: currentMedicine.medicineDetails || '',
            manufacturer: currentMedicine.manufacturer || '',
            supplier: currentMedicine.supplier || '',
            details: currentMedicine.details || 1,
            price: currentMedicine.price || 0, // Keep original price from raw medicine table
            costPerUnit: currentMedicine.costPerUnit || 0, // Keep original costPerUnit from raw medicine table
            userId: user?.id || 1, // Add userId to prevent null reference error
          };
          
          await updateMedicine(medicineUpdateData);
        } catch (medicineError) {
          console.error("Error updating medicine name:", medicineError);
          // Don't throw error here as inventory was already updated successfully
        }
      }

      // Clear editing state
      setEditingCell(null);
      setEditingValue("");
      setOriginalValue("");
      
    } catch (error) {
      console.error("Error saving cell:", error);
      alert("Failed to save changes. Please try again.");
      // Refresh data to revert changes
      await fetchProductList();
    } finally {
      setSaving(false);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditingValue("");
    setOriginalValue("");
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleCellSave();
    } else if (event.key === 'Escape') {
      handleCellCancel();
    }
  };

  // Helper function to render editable cells
  const renderEditableCell = (record, fieldName, value, isNumeric = false) => {
    const isEditing = editingCell?.inventoryId === record.id && editingCell?.columnId === fieldName;
    
    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={editingValue}
            onChange={handleCellChange}
            onKeyDown={handleKeyPress}
            size="small"
            autoFocus
            disabled={saving}
            type={isNumeric ? 'number' : 'text'}
            inputProps={isNumeric ? { min: 0 } : {}}
            sx={{ minWidth: 100 }}
          />
          <IconButton
            size="small"
            onClick={handleCellSave}
            disabled={saving}
            color="primary"
          >
            {saving ? <CircularProgress size={16} /> : <SaveIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCellCancel}
            disabled={saving}
            color="secondary"
          >
            <CancelIcon />
          </IconButton>
        </Box>
      );
    } else {
      // Display value (handle empty/null values)
      const displayValue = value !== null && value !== undefined && value !== '' 
        ? (fieldName === 'expirationDate' && value ? dayjs(value).format("YYYY-MM-DD") : value)
        : (fieldName === 'batchNumber' || fieldName === 'expirationDate' ? 'N/A' : '-');
      
      return (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(record.id, fieldName, value);
          }}
          sx={{ 
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: 1,
            display: 'inline-block',
            minHeight: '20px',
            minWidth: '30px',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            transition: 'background-color 0.2s'
          }}
          title={`Click to edit ${fieldName}`}
        >
          {displayValue}
        </Box>
      );
    }
  };
  DISABLED inventory field editing */

  return (
    <Grid container>
      {!hideMainTitle && (
        <Grid item xs={12} mb={0.5}>
          <Header title="Medicine Inventory" />
        </Grid>
      )}

      <Paper sx={{ width: "100%" }}>
        {/* Tabs for switching between inventory and transaction history */}
        <Box sx={PAGE_TABS_BAR_SX}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="medicine tabs"
            variant="scrollable"
            scrollButtons="auto"
            {...PAGE_TAB_LIST_PROPS}
          >
            <Tab label="Medicine Inventory" value={MEDICINE_TAB.INVENTORY} />
            <Tab label="Transaction History" value={MEDICINE_TAB.TRANSACTION_HISTORY} />
            <Tab label="Raw Medicines" value={MEDICINE_TAB.RAW_MEDICINES} />
            {showAdminMedicineTabs && (
              <Tab label="Supplier Details" value={MEDICINE_TAB.SUPPLIER_DETAILS} />
            )}
            {showAdminMedicineTabs && (
              <Tab label="Near-expiry Settings" value={MEDICINE_TAB.NEAR_EXPIRY_SETTINGS} />
            )}
          </Tabs>
        </Box>

        {activeTab === MEDICINE_TAB.INVENTORY && (
          <Box sx={{ ...TABLE_ACTIONS_ROW_SX, px: 1.5, pt: 1, mb: 0 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={TABLE_ADD_BUTTON_SX}
              onClick={() => {
                setOpenMedicineModal(true);
              }}
            >
              Create Medicine
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={TABLE_ADD_BUTTON_SX}
              onClick={handleAddStockMenuOpen}
              endIcon={<KeyboardArrowDownIcon />}
            >
              Add Stock
            </Button>
          </Box>
        )}

        {/* Tab Panel for Medicine Inventory */}
        {activeTab === MEDICINE_TAB.INVENTORY && (
          <Box>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={async () => await fetchProductList()}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <InventoryStatusLegend sx={{ mt: 0.75 }} />

          {showFilters && (
          <Stack
            spacing={1}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
          >
            <TextField
              size="small"
              label="Name"
              value={filterName}
              onChange={(e)=>setFilterName(e.target.value)}
            />
            <TextField
              label="Category"
              value={filterCategory}
              onChange={(e)=>setFilterCategory(e.target.value)}
              select
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              {[...new Set(product_list.map((p)=>p.medicineType).filter(Boolean))].map((c)=> (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Type"
              value={filterType}
              onChange={(e)=>setFilterType(e.target.value)}
              select
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              {[...new Set(product_list.map((p)=>p.medicineDetails).filter(Boolean))].map((t)=> (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Manufacturer"
              value={filterManufacturer}
              onChange={(e)=>setFilterManufacturer(e.target.value)}
              sx={{ minWidth: 140 }}
            />
            <TextField
              label="Supplier"
              value={filterSupplier}
              onChange={(e)=>setFilterSupplier(e.target.value)}
              sx={{ minWidth: 140 }}
            />
            <TextField
              label="Min Price"
              type="number"
              value={filterPriceMin}
              onChange={(e)=>setFilterPriceMin(e.target.value)}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="Max Price"
              type="number"
              value={filterPriceMax}
              onChange={(e)=>setFilterPriceMax(e.target.value)}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="Min Quantity"
              type="number"
              value={filterQuantityMin}
              onChange={(e)=>setFilterQuantityMin(e.target.value)}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="Max Quantity"
              type="number"
              value={filterQuantityMax}
              onChange={(e)=>setFilterQuantityMax(e.target.value)}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="Batch Number"
              value={filterBatchNumber}
              onChange={(e)=>setFilterBatchNumber(e.target.value)}
              placeholder="Filter by batch number"
            />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell key="expand" style={{ top: 57, width: 50 }}>
                  {/* Expand column */}
                </StyledTableCell>
                {/* Add Inventory ID header when any row is expanded */}
                {expandedRows.size > 0 && (
                  <StyledTableCell key={"inventoryId"} style={{ top: 57 }}>
                    ID
                  </StyledTableCell>
                )}
                <StyledTableCell key="name" style={{ top: 57 }}>Name</StyledTableCell>
                <StyledTableCell key="medicineType" style={{ top: 57 }}>Category</StyledTableCell>
                <StyledTableCell key="medicineDetails" style={{ top: 57 }}>Type</StyledTableCell>
                <StyledTableCell key="price" style={{ top: 57 }}>Price</StyledTableCell>
                <StyledTableCell key="quantity" style={{ top: 57 }}>Total Quantity</StyledTableCell>
                {/* Add Batch Info header when any row is expanded */}
                {expandedRows.size > 0 && (
                  <StyledTableCell key={"batchInfo"} style={{ top: 57 }}>
                    Batch Info
                  </StyledTableCell>
                )}
                <StyledTableCell key={"action"} style={{ top: 57 }}>
                  Action
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {product_list.length === 0 || fetching.product_list ? (
                <TableRow>
                      <TableCell colSpan={columns.length + 1}>
                    {fetching.product_list && <CircularProgress size={40} />}
                    {!fetching.product_list && product_list.length === 0 && (
                      <Alert>No Stock </Alert>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((group) => {
                        const isExpanded = expandedRows.has(group.groupKey);
                    return (
                          <React.Fragment key={group.groupKey}>
                            {/* Main group row */}
                            <TableRow hover>
                              {/* Expand button */}
                              <TableCell sx={{ width: 50 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRowExpansion(group.groupKey)}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              
                              {/* Empty Inventory ID column when expanded (not shown in main row) */}
                              {expandedRows.size > 0 && (
                                <TableCell></TableCell>
                              )}
                              
                              {/* Medicine Name with color coding */}
                              <TableCell>
                                <Box sx={{ 
                                  color: getNameColor(group),
                                  fontWeight: (group.hasExpiredStock || group.hasNearingExpiry || getStockStatus(group.totalQuantity, stockThreshold)) ? 'bold' : 'normal',
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: 0.25,
                                }}>
                                  {group.name}
                                  <InventoryStatusBadges
                                    statusKeys={getGroupInventoryBadgeKeys(group, stockThreshold)}
                                  />
                                </Box>
                              </TableCell>
                              
                              {/* Other columns */}
                              <TableCell>{group.medicineType}</TableCell>
                              <TableCell>{group.medicineDetails}</TableCell>
                              <TableCell>{group.price}</TableCell>
                              <TableCell>{group.totalQuantity}</TableCell>
                              
                              {/* Empty Batch Info column when expanded (not shown in main row) */}
                              {expandedRows.size > 0 && (
                                <TableCell></TableCell>
                              )}
                              <TableCell>
                          <Box>
                              <IconButton
                                sx={{ color: theme.palette.primary.main }}
                                    onClick={() => {
                                        const rawMedicine = medicineData.find(med => med.id === group.medicineId);
                                        const medicineDataToPass = {
                                          id: group.medicineId,
                                          name: group.name,
                                          medicineType: group.medicineType,
                                          manufacturer: group.manufacturer,
                                          supplier: group.supplier,
                                          medicineDetails: group.medicineDetails,
                                          details: rawMedicine?.details || group.details,
                                          price: rawMedicine?.price || 0,
                                          costPerUnit: rawMedicine?.costPerUnit || group.costPerUnit,
                                        };
                                        setProductInfo(medicineDataToPass);
                                        setOpenEditMedicineModal(true);
                                  }}
                              >
                              <ModeEditOutlineTwoToneIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                            
                            {/* Expanded individual records — plain rows; stock/expiry indicators stay on main row */}
                            {isExpanded &&
                              [...(group.records || [])]
                                .sort(
                                  (a, b) =>
                                    Number(b?.id || 0) - Number(a?.id || 0)
                                )
                                .map((record, index) => {
                              const expiryDateValue = record.expirationDate || record.expiryDate || record.expiry;
                              
                              return (
                              <TableRow key={`${group.groupKey}-${record.id || index}`}>
                                {/* Indent indicator */}
                                <TableCell sx={{ pl: 4, width: 50 }}>
                                  <Box sx={{ width: 20, height: 1, backgroundColor: 'grey.300' }} />
                                </TableCell>
                                
                                {/* Inventory ID column - FIRST (always show when expanded) */}
                                <TableCell sx={{ pl: 2 }}>
                                  {record.id}
                                </TableCell>
                                
                                {/* Name - Editable */}
                                <TableCell sx={{ pl: 2 }}>
                                  {renderEditableCell(record, 'name', record.name)}
                                </TableCell>
                                
                                {/* Category */}
                                <TableCell>
                                  {group.medicineType}
                                </TableCell>
                                
                                {/* Type */}
                                <TableCell>
                                  {group.medicineDetails}
                                </TableCell>
                                
                                {/* Price - Editable */}
                                <TableCell>
                                  {renderEditableCell(record, 'price', record.price || 0, true)}
                                </TableCell>
                                
                                {/* Quantity - Editable */}
                                <TableCell>
                                  {renderEditableCell(record, 'quantity', record.quantity, true)}
                                </TableCell>
                                
                                {/* Batch Info: Batch, Expiry, Supplier, Manufacturer */}
                                <TableCell>
                                  <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    <Box sx={{ mb: 0.5 }}>
                                      Batch: {renderEditableCell(record, 'batchNumber', record.batchNumber || 'N/A')}
                                    </Box>
                                    <Box sx={{ mb: 0.5 }}>
                                      Expiry:{" "}
                                      {renderEditableCell(record, 'expirationDate', expiryDateValue || '')}
                                    </Box>
                                    <Box sx={{ mb: 0.5 }}>
                                      Supplier:{" "}
                                      {resolveSupplierDisplayName(record.supplierName) ||
                                        resolveSupplierDisplayName(record.supplier_name) ||
                                        resolveSupplierDisplayName(record.supplier) ||
                                        resolveSupplierDisplayName(group.supplier) ||
                                        "N/A"}
                                    </Box>
                                    <Box sx={{ mb: 0.5 }}>
                                      Invoice No:{" "}
                                      {record.invoiceNumber ||
                                        record.invoice_number ||
                                        "N/A"}
                                    </Box>
                                    <Box>
                                      Manufacturer:{" "}
                                      {record.manufacturer ||
                                        group.manufacturer ||
                                        "N/A"}
                                    </Box>
                                  </Box>
                                </TableCell>
                                
                                {/* Empty Action column */}
                                <TableCell></TableCell>
                              </TableRow>
                              );
                            })}
                          </React.Fragment>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
          </Box>
        )}

        {/* Tab Panel for Transaction History */}
        {activeTab === MEDICINE_TAB.TRANSACTION_HISTORY && (
          <Box>
            <MedicineTransactionHistory
              onViewInventoryBatch={({
                medicineName,
                batchNumber,
                medicineId,
                medicineInventoryId,
              }) => {
                const name = String(medicineName || "").trim();
                const batch = String(batchNumber || "").trim();
                const matchedGroup = findInventoryGroupForTransaction(product_list, {
                  medicineName: name,
                  batchNumber: batch,
                  medicineId,
                  medicineInventoryId,
                });
                const batchStillInStock =
                  Boolean(batch) && inventoryGroupHasBatch(matchedGroup, batch);

                clearFilters();
                setActiveTab(MEDICINE_TAB.INVENTORY);
                setFilterName(
                  matchedGroup?.name || matchedGroup?.groupKey || name
                );
                setFilterBatchNumber(batchStillInStock ? batch : "");
                setShowFilters(batchStillInStock);
                setPage(0);
                setExpandedRows(
                  new Set(
                    [matchedGroup?.groupKey || matchedGroup?.name].filter(Boolean)
                  )
                );

                if (!matchedGroup) {
                  toast.info(
                    "No inventory stock found for this medicine. It may have been fully issued or removed."
                  );
                }
              }}
            />
          </Box>
        )}

        {/* Tab Panel for Supplier Details */}
        {activeTab === MEDICINE_TAB.SUPPLIER_DETAILS && showAdminMedicineTabs && (
          <Box>
            <SupplierDetailsTable inventoryScope="medicine" />
          </Box>
        )}

        {/* Tab Panel for Near-expiry threshold (global) */}
        {activeTab === MEDICINE_TAB.NEAR_EXPIRY_SETTINGS && showAdminMedicineTabs && (
          <Box sx={{ p: 1.5, pt: 1 }}>
            <PharmacyExpiryThresholdSettings />
          </Box>
        )}

        {/* Tab Panel for Raw Medicines */}
        {activeTab === MEDICINE_TAB.RAW_MEDICINES && (
          <Box>
            {fetching.raw_medicines ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
                  <Box sx={TABLE_SEARCH_ROW_SX}>
                    <TextField
                      placeholder="Search"
                      value={rawMedicinesSearchQuery}
                      onChange={(e) => setRawMedicinesSearchQuery(e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Tooltip title={rawMedicinesShowFilters ? "Hide filters" : "Show filters"}>
                      <IconButton
                        size="small"
                        sx={TABLE_ICON_BUTTON_SX}
                        onClick={() => setRawMedicinesShowFilters((v) => !v)}
                      >
                        <FilterAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export to Excel">
                      <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleRawMedicinesExport}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh">
                      <IconButton
                        size="small"
                        sx={TABLE_ICON_BUTTON_SX}
                        onClick={async () => await fetchRawMedicines()}
                        disabled={fetching.raw_medicines}
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {rawMedicinesShowFilters && (
                    <Stack
                      spacing={1}
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
                    >
                      <TextField
                        size="small"
                        label="Name" 
                        value={rawMedicinesFilterName} 
                        onChange={(e) => setRawMedicinesFilterName(e.target.value)} 
                      />
                      <TextField 
                        label="Category" 
                        value={rawMedicinesFilterCategory} 
                        onChange={(e) => setRawMedicinesFilterCategory(e.target.value)} 
                        select 
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {[...new Set(rawMedicines.map((m) => m.medicineType).filter(Boolean))].map((c) => (
                          <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                      </TextField>
                      <TextField 
                        label="Type" 
                        value={rawMedicinesFilterType} 
                        onChange={(e) => setRawMedicinesFilterType(e.target.value)} 
                        select 
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {[...new Set(rawMedicines.map((m) => m.medicineDetails).filter(Boolean))].map((t) => (
                          <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Manufacturer"
                        value={rawMedicinesFilterManufacturer}
                        onChange={(e) => setRawMedicinesFilterManufacturer(e.target.value)}
                      />
                      <TextField
                        label="Min Price"
                        type="number"
                        value={rawMedicinesFilterPriceMin}
                        onChange={(e) => setRawMedicinesFilterPriceMin(e.target.value)}
                        sx={{ maxWidth: 140 }}
                      />
                      <TextField
                        label="Max Price"
                        type="number"
                        value={rawMedicinesFilterPriceMax}
                        onChange={(e) => setRawMedicinesFilterPriceMax(e.target.value)}
                        sx={{ maxWidth: 140 }}
                      />
                      <TextField
                        label="Min Cost/Unit"
                        type="number"
                        value={rawMedicinesFilterCostMin}
                        onChange={(e) => setRawMedicinesFilterCostMin(e.target.value)}
                        sx={{ maxWidth: 140 }}
                      />
                      <TextField
                        label="Max Cost/Unit"
                        type="number"
                        value={rawMedicinesFilterCostMax}
                        onChange={(e) => setRawMedicinesFilterCostMax(e.target.value)}
                        sx={{ maxWidth: 140 }}
                      />
                      <TextField
                        label="Details"
                        value={rawMedicinesFilterDetails}
                        onChange={(e) => setRawMedicinesFilterDetails(e.target.value)}
                      />
                      <Tooltip title="Clear filters">
                        <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearRawMedicinesFilters}>
                          <ClearAllIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
                <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>ID</StyledTableCell>
                      <StyledTableCell>Medicine Name</StyledTableCell>
                      <StyledTableCell>Category</StyledTableCell>
                      <StyledTableCell>Type</StyledTableCell>
                      <StyledTableCell>Manufacturer</StyledTableCell>
                      <StyledTableCell>Price</StyledTableCell>
                      <StyledTableCell>Cost Per Unit</StyledTableCell>
                      <StyledTableCell>Details</StyledTableCell>
                      <StyledTableCell>Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRawMedicines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No raw medicines found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRawMedicines
                        .slice(rawMedicinesPage * rawMedicinesRowsPerPage, rawMedicinesPage * rawMedicinesRowsPerPage + rawMedicinesRowsPerPage)
                        .map((medicine) => (
                        <TableRow key={medicine.id}>
                          <TableCell>{medicine.id}</TableCell>
                          <TableCell>{medicine.medicineName || medicine.name || '-'}</TableCell>
                          <TableCell>{medicine.medicineType || '-'}</TableCell>
                          <TableCell>{medicine.medicineDetails || '-'}</TableCell>
                          <TableCell>{medicine.manufacturer || '-'}</TableCell>
                          <TableCell>{medicine.price || 0}</TableCell>
                          <TableCell>{medicine.costPerUnit || 0}</TableCell>
                          <TableCell>{medicine.details || '-'}</TableCell>
                          <TableCell>
                            <IconButton
                              sx={{ color: theme.palette.primary.main }}
                              onClick={() => {
                                const medicineDataToPass = {
                                  id: medicine.id,
                                  name: medicine.medicineName || medicine.name || '',
                                  medicineName: medicine.medicineName || medicine.name || '',
                                  medicineType: medicine.medicineType || '',
                                  manufacturer: medicine.manufacturer || '',
                                  supplier: medicine.supplier || '',
                                  medicineDetails: medicine.medicineDetails || '',
                                  details: medicine.details || 1,
                                  price: medicine.price || 0,
                                  costPerUnit: medicine.costPerUnit || 0,
                                };
                                setProductInfo(medicineDataToPass);
                                setOpenEditMedicineModal(true);
                              }}
                            >
                              <ModeEditOutlineTwoToneIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component="div"
                  count={filteredRawMedicines.length}
                  rowsPerPage={rawMedicinesRowsPerPage}
                  page={rawMedicinesPage}
                  onPageChange={handleRawMedicinesChangePage}
                  onRowsPerPageChange={handleRawMedicinesChangeRowsPerPage}
                />
              </TableContainer>
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Add Stock Menu */}
      <Menu
        anchorEl={addStockMenuAnchor}
        open={Boolean(addStockMenuAnchor)}
        onClose={handleAddStockMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleManualStockAdd}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manual</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOCRStockAdd}>
          <ListItemIcon>
            <UploadFileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Upload</ListItemText>
        </MenuItem>
      </Menu>

      {/* <AddNewProduct
        open={open_product_modal}
        onClose={async () => {
          setOpenProductModal(false);
          setProductInfo(null);
          await fetchProductList();
        }}
        product_info={product_info}
      /> */}
      <AddMedicineInventory
        open={open_product_modal}
        onClose={async () => {
          setOpenProductModal(false);
          setProductInfo(null);
          await fetchProductList();
        }}
        product_info={product_info}
        refreshData={fetchProductList}
      />
      <AddNewMedicine
        open={open_medicine_modal}
        onClose={async () => {
          setOpenMedicineModal(false);
          await fetchProductList();
        }}
        refreshData={fetchProductList}
      />
      
      {/* Conditionally render the EditMedicineModal */}
      {open_edit_medicine_modal && product_info && (
        <EditMedicineModal
          open={open_edit_medicine_modal}
          // onClose={() => setOpenEditMedicineModal(false)}
          onClose={async () => {
          setOpenEditMedicineModal(false);
          setProductInfo(null);
          // Refresh data based on which tab is active
          if (activeTab === MEDICINE_TAB.RAW_MEDICINES) {
            await fetchRawMedicines();
          } else {
            await fetchProductList();
          }
        }}
          medicineData={product_info} // Pass the selected medicine data
        />
      )}

      {/* OCR Dialog */}
      <OCRDialog
        open={open_ocr_dialog}
        onClose={() => setOpenOCRDialog(false)}
        onOCRComplete={handleOCRComplete}
      />

      {/* OCR Result Dialog */}
      {ocrData && (
        <OCRResultDialog
          open={open_ocr_result_dialog}
          onClose={() => {
            setOpenOCRResultDialog(false);
            setOCRData(null);
          }}
          ocrData={ocrData}
          onConfirm={handleOCRConfirm}
          expiryThresholdMonths={expiryThresholdMonths}
        />
      )}

    </Grid>
  );
}

